import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { CompanyWebsiteAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { StatefulPrimaryButtonComponent } from 'src/app/shared/onboarding/stateful-primary-button/stateful-primary-button.component';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-company-website',
  templateUrl: './company-website.component.html',
  styleUrls: ['./company-website.component.scss'],
})
export class CompanyWebsiteComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  form: FormGroup;
  pageStateId: string;

  questionText: string;
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];
  payload: IBusinessProfile;

  @ViewChild(StatefulPrimaryButtonComponent)
  submitButton: StatefulPrimaryButtonComponent;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private fb: FormBuilder,
    private ees: EnrollmentExtendedService,
    private store: Store<IAppState>,
    private bf: BusinessProfileService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
      })
    );
    this.payload = this.bf.getPayload();
    // Update payload with pagestate id of current page
    this.payload.PageStateId = this.pageStateId;

    this.form = this.fb.group({
      cws: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(http(s)?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-.\/?%&=]*)?$/
          ),
        ],
      ],
    });
    this.form.get('cws')?.setValue(this.payload?.Website);
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    if (this.form.valid) {
      const cwsValue = this.form.get('cws')?.value ?? '';
      this.payload.Website = cwsValue;
      const res = await this.bf.submitBusinessProfileData(this.payload);

      // saving to store
      this.store.dispatch(
        CompanyWebsiteAction({
          companyWebsite: cwsValue,
        })
      );
      return true;
    }
    this.form.markAllAsTouched();
    return false;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      'Do you have a Company Website?';
  }

  onEnter() {
    this.submitButton.submit();
  }
}
